import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Container,
  Group,
  Loader,
  NumberInput,
  Paper,
  Radio,
  Select,
  Stack,
  Stepper,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import {
  IconCalendarTime,
  IconFlagCheck,
  IconGridDots,
  IconMessageCircle,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PortalHero } from '../components/PortalHero';
import { tokens } from '../ui/tokens';
import dayjs from 'dayjs';
import { useVenueProfile } from '../profile-context';
import {
  createPublicReservation,
  fetchAvailability,
  type AvailabilityResult,
  type PublicVenueProfile,
} from '../api-client';

/**
 * Guest booking flow, time-first.
 *
 *   Step 0  When  — date + start time + duration + party size
 *   Step 1  Where — zones filtered by partySize bounds + live availability
 *   Step 2  Who   — name + at least one of phone / email / line
 *   Step 3  Confirm + send
 *
 * Rationale: guests think in terms of "I need X people on date Y at time Z".
 * Zone / resource selection is a secondary consequence, not a primary axis,
 * so we let them commit to the time first and surface only zones that can
 * actually serve that commitment.
 */

function generateTimeSlots(): string[] {
  const slots: string[] = [];
  for (let h = 10; h <= 21; h++) {
    slots.push(`${String(h).padStart(2, '0')}:00`);
    if (h < 21) slots.push(`${String(h).padStart(2, '0')}:30`);
  }
  return slots;
}

const SLOT_OPTIONS = generateTimeSlots().map((s) => ({ value: s, label: s }));

type ZoneRow = PublicVenueProfile['zones'][number];

interface ZoneAvailabilityState {
  zone: ZoneRow;
  partyFits: boolean;
  availability: AvailabilityResult | null;
  error: string | null;
  loading: boolean;
}

const PARTY_BADGE_COLOR: Record<AvailabilityResult['busyLevel'] | 'none', string> = {
  low: 'teal',
  medium: 'yellow',
  high: 'orange',
  full: 'red',
  none: 'gray',
};

const PARTY_BADGE_LABEL: Record<AvailabilityResult['busyLevel'] | 'none', string> = {
  low: '空位多',
  medium: '部分可預訂',
  high: '少數可預訂',
  full: '此時段已滿',
  none: '—',
};

export function BookingFlowPage(): JSX.Element {
  const profile = useVenueProfile();
  const navigate = useNavigate();

  const [active, setActive] = useState(0);

  // ── Step 0: time + party ──
  const [date, setDate] = useState<string>(dayjs().add(1, 'day').format('YYYY-MM-DD'));
  const [startTime, setStartTime] = useState<string>('18:00');
  const [durationHours, setDurationHours] = useState<number>(1);
  const [partySize, setPartySize] = useState<number | ''>(2);

  const startIso = useMemo(() => {
    if (!date || !startTime) return null;
    return dayjs.tz(`${date}T${startTime}:00`, profile.timezone).utc().toISOString();
  }, [date, startTime, profile.timezone]);
  const endIso = useMemo(() => {
    if (!startIso) return null;
    return dayjs(startIso).add(durationHours, 'hour').toISOString();
  }, [startIso, durationHours]);

  const canNextFromStep0 =
    typeof partySize === 'number' &&
    partySize >= 1 &&
    partySize <= 20 &&
    Boolean(startIso) &&
    Boolean(endIso) &&
    durationHours >= 1;

  // ── Step 1: zone selection (live availability) ──
  const bookableZones = useMemo(
    () => profile.zones.filter((z) => z.scenarioType !== 'PICKUP_ORDER'),
    [profile.zones],
  );
  const [zoneStates, setZoneStates] = useState<ZoneAvailabilityState[]>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  async function loadAvailability(): Promise<void> {
    if (!canNextFromStep0 || typeof partySize !== 'number' || !startIso || !endIso) return;
    setAvailabilityLoading(true);
    setSelectedZoneId(null);
    // First pass: render rows immediately with loading state.
    const initial: ZoneAvailabilityState[] = bookableZones.map((z) => ({
      zone: z,
      partyFits: partySize >= z.minPartySize && partySize <= z.maxPartySize,
      availability: null,
      error: null,
      loading: partySize >= z.minPartySize && partySize <= z.maxPartySize,
    }));
    setZoneStates(initial);

    const results = await Promise.allSettled(
      initial.map(async (row) => {
        if (!row.partyFits) return { ...row, loading: false };
        try {
          const a = await fetchAvailability({
            venueId: profile.venueId,
            serviceId: row.zone.serviceId,
            scenarioType: row.zone.scenarioType,
            resourceId: row.zone.resourceId,
            partySize,
            startTime: startIso,
            endTime: endIso,
          });
          return { ...row, availability: a, loading: false };
        } catch (e) {
          return {
            ...row,
            error: e instanceof Error ? e.message : String(e),
            loading: false,
          };
        }
      }),
    );
    const next: ZoneAvailabilityState[] = results.map((r, i) =>
      r.status === 'fulfilled'
        ? r.value
        : { ...initial[i]!, loading: false, error: 'lookup failed' },
    );
    setZoneStates(next);

    // Auto-select when exactly one zone is eligible — saves a click on single-zone venues.
    const eligible = next.filter(
      (s) => s.partyFits && s.availability !== null && s.availability.hasAvailability,
    );
    if (eligible.length === 1) setSelectedZoneId(eligible[0]!.zone.serviceId);

    setAvailabilityLoading(false);
  }

  const canNextFromStep1 = selectedZoneId !== null;
  const selectedZone = useMemo(
    () => zoneStates.find((s) => s.zone.serviceId === selectedZoneId)?.zone ?? null,
    [zoneStates, selectedZoneId],
  );

  // ── Step 2: contact ──
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [lineId, setLineId] = useState('');
  const [notes, setNotes] = useState('');

  const hasContact =
    phone.trim().length > 0 || email.trim().length > 0 || lineId.trim().length > 0;
  const canNextFromStep2 = displayName.trim().length > 0 && hasContact;

  // ── Step 3: confirm ──
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    if (!selectedZone || !startIso || !endIso || typeof partySize !== 'number') return;
    setError(null);
    setSubmitting(true);
    try {
      const contact: { displayName: string; phone?: string; email?: string; lineId?: string } = {
        displayName: displayName.trim(),
      };
      if (phone.trim()) contact.phone = phone.trim();
      if (email.trim()) contact.email = email.trim();
      if (lineId.trim()) contact.lineId = lineId.trim();
      const input: Parameters<typeof createPublicReservation>[0] = {
        venueId: profile.venueId,
        serviceId: selectedZone.serviceId,
        scenarioType: selectedZone.scenarioType,
        partySize,
        startTime: startIso,
        endTime: endIso,
        guestContact: contact,
      };
      if (notes.trim()) input.notes = notes.trim();
      const result = await createPublicReservation(input);
      navigate(`/reservations/${result.guestToken}?new=1`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function advanceFromStep0(): Promise<void> {
    setActive(1);
    await loadAvailability();
  }

  useEffect(() => {
    // Reset zone selection whenever the user steps back and changes time / party.
    if (active === 0) setSelectedZoneId(null);
  }, [active]);

  return (
    <Container size="sm" py={{ base: 'md', sm: 'xl' }} px={{ base: 'sm', sm: 'md' }}>
      <Stack gap="lg">
        <PortalHero />

        <Stepper
          active={active}
          onStepClick={setActive}
          size="sm"
          allowNextStepsSelect={false}
          color="sunny"
          iconSize={36}
        >
          <Stepper.Step
            label="時間 · 人數"
            description="什麼時候來？幾個人？"
            icon={<IconCalendarTime size={20} />}
          >
            <Paper p="md" radius="md" withBorder mt="md">
              <Stack>
                <TextInput
                  label="日期"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.currentTarget.value)}
                  withAsterisk
                />
                <Group grow>
                  <Select
                    label="開始時間"
                    data={SLOT_OPTIONS}
                    value={startTime}
                    onChange={(v) => setStartTime(v ?? '18:00')}
                    withAsterisk
                  />
                  <NumberInput
                    label="時長（小時）"
                    min={1}
                    max={6}
                    step={0.5}
                    value={durationHours}
                    onChange={(v) => setDurationHours(typeof v === 'number' ? v : 1)}
                  />
                </Group>
                <NumberInput
                  label="人數"
                  min={1}
                  max={20}
                  value={partySize}
                  onChange={(v) => setPartySize(typeof v === 'number' ? v : '')}
                  withAsterisk
                />
              </Stack>
            </Paper>
          </Stepper.Step>

          <Stepper.Step
            label="區域"
            description="可以接你的場地"
            icon={<IconGridDots size={20} />}
          >
            <Paper p="md" radius="md" withBorder mt="md">
              {availabilityLoading && (
                <Stack align="center" py="md">
                  <Loader />
                  <Text size="xs" c="dimmed">
                    查詢可用區域中…
                  </Text>
                </Stack>
              )}
              {!availabilityLoading && zoneStates.length === 0 && (
                <Text size="sm" c="dimmed">
                  返回上一步以載入時段資訊。
                </Text>
              )}
              {!availabilityLoading && zoneStates.length > 0 && (
                <Radio.Group value={selectedZoneId} onChange={setSelectedZoneId}>
                  <Stack gap="sm">
                    {zoneStates.map((s) => {
                      const busy =
                        s.availability === null ? ('none' as const) : s.availability.busyLevel;
                      const reason = !s.partyFits
                        ? `容量不符 (${s.zone.minPartySize}–${s.zone.maxPartySize} 人)`
                        : s.error
                          ? `查詢失敗：${s.error}`
                          : s.availability !== null && !s.availability.hasAvailability
                            ? '此時段已滿'
                            : null;
                      const disabled = reason !== null;
                      return (
                        <Card
                          key={s.zone.serviceId}
                          p="md"
                          radius="md"
                          withBorder
                          style={{
                            opacity: disabled ? 0.6 : 1,
                            borderColor:
                              selectedZoneId === s.zone.serviceId
                                ? tokens.color.selectedBorder
                                : undefined,
                            borderWidth: selectedZoneId === s.zone.serviceId ? 2 : 1,
                          }}
                        >
                          <Group justify="space-between" wrap="nowrap">
                            <Radio
                              value={s.zone.serviceId}
                              disabled={disabled}
                              label={
                                <Stack gap={2}>
                                  <Text fw={600}>{s.zone.zoneLabel}</Text>
                                  <Text size="xs" c="dimmed">
                                    {s.zone.name} · {s.zone.minPartySize}–{s.zone.maxPartySize} 人
                                  </Text>
                                </Stack>
                              }
                            />
                            <Badge color={PARTY_BADGE_COLOR[busy]} variant="light">
                              {reason ?? PARTY_BADGE_LABEL[busy]}
                            </Badge>
                          </Group>
                        </Card>
                      );
                    })}
                  </Stack>
                </Radio.Group>
              )}
            </Paper>
          </Stepper.Step>

          <Stepper.Step
            label="聯絡"
            description="怎麼通知你？"
            icon={<IconMessageCircle size={20} />}
          >
            <Paper p="md" radius="md" withBorder mt="md">
              <Stack>
                <TextInput
                  label="姓名"
                  placeholder="王小明"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.currentTarget.value)}
                  withAsterisk
                  maxLength={100}
                />
                <TextInput
                  label="手機"
                  placeholder="0912000000"
                  value={phone}
                  onChange={(e) => setPhone(e.currentTarget.value)}
                  maxLength={50}
                />
                <TextInput
                  label="Email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  maxLength={200}
                />
                <TextInput
                  label="LINE ID（選填）"
                  placeholder="@your-line-id"
                  value={lineId}
                  onChange={(e) => setLineId(e.currentTarget.value)}
                  maxLength={50}
                />
                <Textarea
                  label="備註（選填）"
                  placeholder="特殊需求 / 過敏 / 生日慶祝等"
                  value={notes}
                  onChange={(e) => setNotes(e.currentTarget.value)}
                  maxLength={500}
                  autosize
                  minRows={2}
                />
                {!hasContact && (
                  <Text size="xs" c="red">
                    請至少提供一種聯絡方式（手機 / Email / LINE）。
                  </Text>
                )}
              </Stack>
            </Paper>
          </Stepper.Step>

          <Stepper.Completed>
            <Card p="md" radius="md" withBorder mt="md">
              <Stack>
                <Group justify="space-between">
                  <Text fw={600}>{profile.branding.bookingTerm ?? '訂位'}摘要</Text>
                  <Badge>{selectedZone?.zoneLabel ?? '—'}</Badge>
                </Group>
                <Group gap="xs">
                  <Text size="sm" c="dimmed">
                    時間
                  </Text>
                  <Text size="sm">
                    {date} {startTime} · {durationHours} 小時
                  </Text>
                </Group>
                <Group gap="xs">
                  <Text size="sm" c="dimmed">
                    人數
                  </Text>
                  <Text size="sm">{partySize} 位</Text>
                </Group>
                <Group gap="xs">
                  <Text size="sm" c="dimmed">
                    姓名
                  </Text>
                  <Text size="sm">{displayName}</Text>
                </Group>
                <Checkbox
                  checked={agreed}
                  onChange={(e) => setAgreed(e.currentTarget.checked)}
                  label="同意到達前 2 小時外可自行線上取消，逾時請來電或訊息聯繫。"
                />
                {error && (
                  <Alert color="red" title="送出失敗">
                    {error}
                  </Alert>
                )}
              </Stack>
            </Card>
          </Stepper.Completed>
        </Stepper>

        <Group justify="space-between">
          <Button
            variant="default"
            onClick={() => setActive((s) => Math.max(0, s - 1))}
            disabled={active === 0 || submitting}
          >
            上一步
          </Button>
          {active === 0 && (
            <Button
              className="portal-cta"
              size="md"
              radius="xl"
              onClick={() => void advanceFromStep0()}
              disabled={!canNextFromStep0}
            >
              看哪個場地有空 →
            </Button>
          )}
          {active === 1 && (
            <Button
              className="portal-cta"
              size="md"
              radius="xl"
              onClick={() => setActive(2)}
              disabled={!canNextFromStep1}
            >
              填聯絡資訊 →
            </Button>
          )}
          {active === 2 && (
            <Button
              className="portal-cta"
              size="md"
              radius="xl"
              onClick={() => setActive(3)}
              disabled={!canNextFromStep2}
            >
              最後確認 →
            </Button>
          )}
          {active === 3 && (
            <Button
              className="portal-cta"
              size="md"
              radius="xl"
              onClick={() => void submit()}
              loading={submitting}
              disabled={!agreed}
            >
              送出，準備來爆汗
            </Button>
          )}
        </Group>
      </Stack>
    </Container>
  );
}
